import { useMemo } from 'react';
import {
  Eye,
  ExternalLink,
  Info,
  MapPin,
  MonitorSmartphone,
  MousePointerClick,
  Search as SearchIcon,
  TrendingUp,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatTile } from '@/components/marketing/MetricTile';
import { RankedList } from '@/components/marketing/RankedList';
import { TrendChart } from '@/components/marketing/TrendChart';
import {
  ChannelStatusNotice,
  resolveChannelStatus,
} from '@/components/marketing/ChannelStatusNotice';
import { comparisonLabel } from '@/components/marketing/RangeSelector';
import { CHART_SERIES } from '@/lib/brand';
import { formatDate } from '@/lib/formatters';
import { useSearchConsole, type SearchRankedRow } from '@/features/marketing/hooks';
import {
  formatCompactNumber,
  formatRatioPercent,
  pctChange,
  pctChangeInverted,
  type MarketingRange,
} from '@/features/marketing/range';

interface SearchTabProps {
  range: MarketingRange;
}

/**
 * Organic search performance from Google Search Console.
 *
 * Two things about this tab differ from the others and are surfaced in the UI
 * rather than buried:
 *
 *  - Search Console data lags 2-3 days behind real time, so the most recent
 *    day here is never "today". The freshness note states the actual latest
 *    day in the data so nobody reads the tail of the chart as a traffic drop.
 *
 *  - Average position is impressions-weighted (see `weightedMean`) and lower
 *    is better, so its delta is inverted — an improvement in ranking shows as
 *    a positive change.
 */
export function SearchTab({ range }: SearchTabProps) {
  const query = useSearchConsole(range);
  const data = query.data;
  const totals = data?.totals;
  const previous = data?.previous;
  const deltaLabel = comparisonLabel(range);
  const loading = query.isLoading;

  const status = resolveChannelStatus({
    isLoading: query.isLoading,
    error: query.error,
    connected: data?.connected,
  });

  // Queries earning impressions but few clicks are the actionable list on this
  // tab — they're rankings the site already has and isn't converting into
  // visits. Requiring a real impression floor keeps one-off long-tail noise
  // out of what's meant to be a work queue.
  const opportunities = useMemo(() => {
    const rows = data?.queries ?? [];
    if (rows.length === 0) return [];
    const impressionFloor = Math.max(
      50,
      Math.round(
        rows.reduce((sum, row) => sum + row.impressions, 0) / Math.max(1, rows.length),
      ),
    );
    return rows
      .filter((row) => row.impressions >= impressionFloor && row.position > 5)
      .sort((a, b) => b.impressions - a.impressions)
      .slice(0, 8);
  }, [data?.queries]);

  return (
    <div className="space-y-6">
      <ChannelStatusNotice
        status={status}
        channel="Google Search Console"
        error={query.error instanceof Error ? query.error.message : null}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium tracking-headline text-foreground">
            Organic search
          </h2>
        </div>
        <Button variant="outline" size="sm" asChild>
          <a href="https://search.google.com/search-console" target="_blank" rel="noreferrer">
            Open Search Console <ExternalLink className="h-4 w-4" />
          </a>
        </Button>
      </div>

      {data?.connected && data.latestDay && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3 w-3 shrink-0" strokeWidth={1.8} />
          <span>
            Search Console reports on a 2–3 day delay. Latest complete day in
            this view: {formatDate(data.latestDay)}.
          </span>
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Clicks"
          icon={SearchIcon}
          loading={loading}
          value={formatCompactNumber(totals?.clicks ?? 0)}
          delta={pctChange(totals?.clicks ?? 0, previous?.clicks ?? 0)}
          deltaLabel={deltaLabel}
        />
        <StatTile
          label="Impressions"
          icon={Eye}
          loading={loading}
          value={formatCompactNumber(totals?.impressions ?? 0)}
          delta={pctChange(totals?.impressions ?? 0, previous?.impressions ?? 0)}
          deltaLabel={deltaLabel}
        />
        <StatTile
          label="Click-through rate"
          icon={MousePointerClick}
          loading={loading}
          value={formatRatioPercent(totals?.ctr ?? 0, 2)}
          delta={pctChange(totals?.ctr ?? 0, previous?.ctr ?? 0)}
          deltaLabel={deltaLabel}
        />
        <StatTile
          label="Average position"
          icon={TrendingUp}
          loading={loading}
          value={totals?.position ? totals.position.toFixed(1) : '—'}
          delta={pctChangeInverted(totals?.position ?? 0, previous?.position ?? 0)}
          deltaLabel={deltaLabel}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Clicks and impressions</CardTitle>
        </CardHeader>
        <CardContent>
          <TrendChart
            data={data?.daily ?? []}
            height={300}
            formatRightAxis={formatCompactNumber}
            formatLeftAxis={formatCompactNumber}
            series={[
              { key: 'clicks', label: 'Clicks', color: CHART_SERIES[0], kind: 'area' },
              {
                key: 'impressions',
                label: 'Impressions',
                color: CHART_SERIES[3],
                kind: 'line',
                axis: 'right',
              },
            ]}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Top queries</CardTitle>
          </CardHeader>
          <CardContent>
            <RankedList
              rows={(data?.queries ?? []).slice(0, 10).map(queryToRow)}
              formatValue={formatCompactNumber}
              emptyLabel="No query data for this period."
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Ranking opportunities</CardTitle>
          </CardHeader>
          <CardContent>
            <RankedList
              rows={opportunities.map((row) => ({
                key: row.key,
                label: row.label,
                value: row.impressions,
                hint: `position ${row.position.toFixed(1)} · ${formatRatioPercent(row.ctr, 2)} CTR`,
                meta: `${formatCompactNumber(row.clicks)} clicks`,
              }))}
              formatValue={formatCompactNumber}
              emptyLabel="No high-impression queries ranking below position 5 — nothing obvious to chase."
            />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Top landing pages</CardTitle>
          </CardHeader>
          <CardContent>
            <RankedList
              rows={(data?.pages ?? []).slice(0, 8).map((row) => ({
                key: row.key,
                label: shortenUrl(row.label),
                href: row.key,
                value: row.clicks,
                share: row.share,
                hint: `${formatCompactNumber(row.impressions)} impressions · position ${row.position.toFixed(1)}`,
              }))}
              formatValue={formatCompactNumber}
              emptyLabel="No landing-page data for this period."
            />
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MonitorSmartphone className="h-4 w-4 text-muted-foreground" />
                Devices
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RankedList
                compact
                rows={(data?.devices ?? []).map((row) => ({
                  key: row.key,
                  label: row.label,
                  value: row.clicks,
                  share: row.share,
                }))}
                formatValue={formatCompactNumber}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                Countries
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RankedList
                compact
                rows={(data?.countries ?? []).slice(0, 6).map((row) => ({
                  key: row.key,
                  label: row.label,
                  value: row.clicks,
                  share: row.share,
                }))}
                formatValue={formatCompactNumber}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function queryToRow(row: SearchRankedRow) {
  return {
    key: row.key,
    label: row.label,
    value: row.clicks,
    share: row.share,
    hint: `${formatCompactNumber(row.impressions)} impressions · ${formatRatioPercent(row.ctr, 2)} CTR · position ${row.position.toFixed(1)}`,
  };
}

/**
 * Search Console reports full URLs (domain properties can span subdomains), but
 * the host is identical for nearly every row — so we show the path and let the
 * link carry the full address.
 */
function shortenUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}` || '/';
  } catch {
    return url;
  }
}
